import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InventorySeriesResult, SeriesBucket } from 'interfaces/report.interface';
import { formatNumber, formatTick, formatTooltipTime } from 'utils/report.utils';
import './volume-chart.style.scss';

/**
 * Categorical slots, in fixed order. Validated for adjacent-pair CVD separation and
 * normal-vision separation on a light surface. Never cycle past slot 8 - a 9th tank
 * is left out of the chart rather than reusing a hue.
 */
const SERIES_COLORS = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#4a3aa7',
  '#e34948',
];

export const MAX_CHART_SERIES = SERIES_COLORS.length;

const VIEW_W = 960;
const VIEW_H = 340;
const PAD = { top: 16, right: 108, bottom: 40, left: 72 };
const PLOT_W = VIEW_W - PAD.left - PAD.right;
const PLOT_H = VIEW_H - PAD.top - PAD.bottom;

const Y_TICKS = 4;
const X_TICKS = 6;

type Plotted = {
  tankId: number;
  name: string;
  color: string;
  points: { t: number; v: number }[];
};

interface IProps {
  data: InventorySeriesResult | null;
  /**
   * Stable tankId -> colour slot map derived from every configured tank, so that
   * filtering the report never repaints the tanks that remain.
   */
  colorSlots: Map<number, number>;
}

const VolumeChart: React.FC<IProps> = ({ data, colorSlots }) => {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverT, setHoverT] = useState<number | null>(null);

  const bucket: SeriesBucket = data?.bucket ?? 'DAY';

  const { series, tMin, tMax, yMin, yMax, hiddenCount } = useMemo(() => {
    const raw = data?.series ?? [];

    // Normally slots come from the full configured tank list. If that lookup is empty
    // - the tank tree failed to load - fall back to slots derived from the data itself
    // so the chart still renders; only then can a filter repaint.
    const slotFor: Map<number, number> = colorSlots.size
      ? colorSlots
      : new Map(
          // The series arrive already ordered by KPU then Qoldau tank id, so index
          // order is the display order; sorting by raw id would scramble it.
          raw.map((s, index) => [s.tankId, index]),
        );

    const plotted: Plotted[] = raw
      .filter((s) => (slotFor.get(s.tankId) ?? MAX_CHART_SERIES) < MAX_CHART_SERIES)
      .sort((a, b) => (slotFor.get(a.tankId) ?? 0) - (slotFor.get(b.tankId) ?? 0))
      .map((s) => ({
        tankId: s.tankId,
        name: s.tankName || `#${s.tankId}`,
        color: SERIES_COLORS[slotFor.get(s.tankId) ?? 0],
        points: s.points
          .filter((p) => p.avgVolume !== null)
          .map((p) => ({ t: new Date(p.t).getTime(), v: p.avgVolume as number }))
          .filter((p) => !Number.isNaN(p.t))
          .sort((a, b) => a.t - b.t),
      }))
      .filter((s) => s.points.length > 0);

    const allT = plotted.flatMap((s) => s.points.map((p) => p.t));
    const allV = plotted.flatMap((s) => s.points.map((p) => p.v));

    const loT = allT.length ? Math.min(...allT) : 0;
    const hiT = allT.length ? Math.max(...allT) : 1;
    const loV = allV.length ? Math.min(...allV) : 0;
    const hiV = allV.length ? Math.max(...allV) : 1;

    // Pad the value domain so lines never sit on the frame. Tank levels are read as
    // change over time, so the scale is not pinned to zero - but it must not run
    // below zero either, which the padding would otherwise do when several tanks of
    // very different sizes share the axis.
    const spread = hiV - loV || Math.abs(hiV) || 1;
    const paddedMin = loV - spread * 0.1;

    return {
      series: plotted,
      tMin: loT,
      tMax: hiT,
      yMin: loV >= 0 ? Math.max(0, paddedMin) : paddedMin,
      yMax: hiV + spread * 0.1,
      hiddenCount: raw.length - plotted.length,
    };
  }, [data, colorSlots]);

  const xFor = (t: number) =>
    tMax === tMin ? PAD.left + PLOT_W / 2 : PAD.left + ((t - tMin) / (tMax - tMin)) * PLOT_W;

  const yFor = (v: number) => PAD.top + PLOT_H - ((v - yMin) / (yMax - yMin || 1)) * PLOT_H;

  const yTicks = useMemo(
    () => Array.from({ length: Y_TICKS + 1 }, (_, i) => yMin + ((yMax - yMin) * i) / Y_TICKS),
    [yMin, yMax],
  );

  /**
   * Enough decimals for the ticks to actually differ. A single tank can span less than
   * a cubic metre across the whole window, where whole numbers would print the same
   * label five times.
   */
  const yTickDigits = useMemo(() => {
    const step = (yMax - yMin) / Y_TICKS;
    if (step >= 10) return 0;
    if (step >= 1) return 1;
    if (step >= 0.1) return 2;
    return 3;
  }, [yMin, yMax]);

  // Evenly spaced ticks across the real time span, so gaps in the data stay visible
  const xTicks = useMemo(
    () =>
      tMax === tMin
        ? [tMin]
        : Array.from({ length: X_TICKS + 1 }, (_, i) => tMin + ((tMax - tMin) * i) / X_TICKS),
    [tMin, tMax],
  );

  const handleMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || series.length === 0) return;
    const rect = svg.getBoundingClientRect();
    // Map client px back into viewBox units - the SVG scales to its container
    const x = ((event.clientX - rect.left) / rect.width) * VIEW_W;
    const ratio = Math.min(1, Math.max(0, (x - PAD.left) / (PLOT_W || 1)));
    setHoverT(tMin + ratio * (tMax - tMin));
  };

  if (series.length === 0) {
    return <p className="volumeChart__empty">{t('report.inventory.chart.noData')}</p>;
  }

  /** Nearest actual sample to the cursor, per series. */
  const hovered =
    hoverT === null
      ? []
      : series
          .map((s) => {
            const nearest = s.points.reduce((best, p) =>
              Math.abs(p.t - hoverT) < Math.abs(best.t - hoverT) ? p : best,
            );
            return { s, p: nearest };
          })
          .filter(({ p }) => {
            // Ignore a series whose nearest sample is far from the cursor
            const tolerance = (tMax - tMin) / 40 || 1;
            return Math.abs(p.t - hoverT) <= tolerance;
          });

  const crosshairT = hovered.length ? hovered[0].p.t : null;

  return (
    <div className="volumeChart">
      <div className="volumeChart__meta">
        {t(`report.inventory.chart.bucket.${bucket}`)}
      </div>

      <svg
        ref={svgRef}
        className="volumeChart__svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="img"
        aria-label={String(t('report.inventory.chart.title'))}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverT(null)}
      >
        {yTicks.map((tick, i) => (
          <g key={`y_${i}`}>
            <line
              className="volumeChart__grid"
              x1={PAD.left}
              x2={PAD.left + PLOT_W}
              y1={yFor(tick)}
              y2={yFor(tick)}
            />
            <text
              className="volumeChart__axisText"
              x={PAD.left - 10}
              y={yFor(tick) + 4}
              textAnchor="end"
            >
              {formatNumber(tick, yTickDigits)}
            </text>
          </g>
        ))}

        {xTicks.map((tick, i) => (
          <text
            key={`x_${i}`}
            className="volumeChart__axisText"
            x={xFor(tick)}
            y={PAD.top + PLOT_H + 24}
            textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
          >
            {formatTick(tick, bucket)}
          </text>
        ))}

        {crosshairT !== null && (
          <line
            className="volumeChart__crosshair"
            x1={xFor(crosshairT)}
            x2={xFor(crosshairT)}
            y1={PAD.top}
            y2={PAD.top + PLOT_H}
          />
        )}

        {series.map((s) => {
          // Break the path where the gap between samples is much larger than the
          // bucket, so a period with no readings reads as a hole rather than a line
          // drawn straight through it.
          const gapLimit = bucketGapLimit(bucket);
          const segments: string[] = [];
          let current: string[] = [];

          s.points.forEach((p, i) => {
            const prev = s.points[i - 1];
            if (prev && p.t - prev.t > gapLimit) {
              if (current.length > 1) segments.push(current.join(' '));
              current = [];
            }
            current.push(`${current.length ? 'L' : 'M'}${xFor(p.t)},${yFor(p.v)}`);
          });
          if (current.length > 1) segments.push(current.join(' '));

          const last = s.points[s.points.length - 1];
          const hoverPoint = hovered.find((h) => h.s.tankId === s.tankId)?.p;

          return (
            <g key={s.tankId}>
              {segments.map((d, i) => (
                <path key={i} className="volumeChart__line" d={d} stroke={s.color} />
              ))}

              {/* A lone sample has no line to draw, so mark it explicitly */}
              {s.points.length === 1 && (
                <circle
                  className="volumeChart__marker"
                  cx={xFor(s.points[0].t)}
                  cy={yFor(s.points[0].v)}
                  r={5}
                  fill={s.color}
                />
              )}

              {hoverPoint && (
                <circle
                  className="volumeChart__marker"
                  cx={xFor(hoverPoint.t)}
                  cy={yFor(hoverPoint.v)}
                  r={5}
                  fill={s.color}
                />
              )}

              {/* Direct labels double as the relief for low-contrast slots. Tank
                  designations are long, so they are clipped to the right gutter and
                  carry the full name as a tooltip. */}
              {series.length <= 4 && (
                <text
                  className="volumeChart__seriesLabel"
                  x={Math.min(xFor(last.t) + 10, VIEW_W - PAD.right + 8)}
                  y={yFor(last.v) + 4}
                >
                  <title>{s.name}</title>
                  {truncate(s.name, 16)}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {crosshairT !== null && hovered.length > 0 && (
        <div
          className="volumeChart__tooltip"
          style={{ left: `${(xFor(crosshairT) / VIEW_W) * 100}%` }}
        >
          <div className="volumeChart__tooltipDay">{formatTooltipTime(crosshairT, bucket)}</div>
          {hovered.map(({ s, p }) => (
            <div className="volumeChart__tooltipRow" key={s.tankId}>
              <span className="volumeChart__swatch" style={{ background: s.color }} />
              <span className="volumeChart__tooltipName">{s.name}</span>
              <span className="volumeChart__tooltipValue">{formatNumber(p.v)}</span>
            </div>
          ))}
        </div>
      )}

      {series.length >= 2 && (
        <div className="volumeChart__legend">
          {series.map((s) => (
            <span className="volumeChart__legendItem" key={s.tankId}>
              <span className="volumeChart__swatch" style={{ background: s.color }} />
              {s.name}
            </span>
          ))}
        </div>
      )}

      {hiddenCount > 0 && (
        <p className="volumeChart__note">
          {t('report.inventory.chart.tooManyTanks', { count: hiddenCount })}
        </p>
      )}
    </div>
  );
};

const truncate = (value: string, max: number): string =>
  value.length <= max ? value : `${value.slice(0, max - 1).trimEnd()}…`;

/** A gap wider than ~2.5 buckets is treated as missing data rather than a straight run. */
const bucketGapLimit = (bucket: SeriesBucket): number => {
  const minute = 60_000;
  switch (bucket) {
    case 'MINUTE':
      return 2.5 * minute;
    case 'HOUR':
      return 2.5 * 60 * minute;
    case 'DAY':
      return 2.5 * 24 * 60 * minute;
    case 'WEEK':
      return 2.5 * 7 * 24 * 60 * minute;
  }
};

export default VolumeChart;
